import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { signOut } from '@/services/auth.service';
import { useToast } from '@/hooks/use-toast';
import { logButtonClick, logError } from '@/services/analytics.service';
import { UserMenuComponent } from './UserMenu.component';

interface UserMenuProps {
  variant?: 'default' | 'board';
  hideCreateBoard?: boolean;
}

export const UserMenu = ({
  variant = 'default',
  hideCreateBoard = false,
}: UserMenuProps) => {
  const { user, firebaseUser } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [createBoardDialogOpen, setCreateBoardDialogOpen] = useState(false);

  const photoURL = firebaseUser?.photoURL;

  if (!user) return null;

  const handleSignOut = async () => {
    logButtonClick('Sign Out', window.location.pathname);
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to sign out';
      logError('Sign Out Failed', errorMessage, 'UserMenu');
      toast({
        title: 'Sign out failed',
        description: 'Failed to sign out. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleMyProfileClick = () => {
    logButtonClick('My Profile', 'UserMenu');
    navigate(`/user/${user.username}`);
  };

  const handleBrowseBoardsClick = () => {
    logButtonClick('Browse Boards', 'UserMenu');
    navigate('/browse');
  };

  const handleCreateBoardClick = () => {
    logButtonClick('Create Board', 'UserMenu');
    setCreateBoardDialogOpen(true);
  };

  return (
    <UserMenuComponent
      user={user}
      photoURL={photoURL || null}
      variant={variant}
      hideCreateBoard={hideCreateBoard}
      createBoardDialogOpen={createBoardDialogOpen}
      onCreateBoardDialogOpenChange={setCreateBoardDialogOpen}
      onMyProfileClick={handleMyProfileClick}
      onBrowseBoardsClick={handleBrowseBoardsClick}
      onSignOut={handleSignOut}
      onCreateBoardClick={handleCreateBoardClick}
    />
  );
};

